import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Button,
  Space,
  Tag,
  Progress,
  Alert,
  Tabs,
  List,
  Avatar,
  Typography,
  Badge,
  Tooltip,
  Select,
  DatePicker,
  Input,
} from 'antd';
import {
  HomeOutlined,
  ScanOutlined,
  AlertOutlined,
  TruckOutlined,
  BoxPlotOutlined,
  BarChartOutlined,
  SearchOutlined,
  PlusOutlined,
  ExportOutlined,
  ReloadOutlined,
  SettingOutlined,
  QrcodeOutlined,
} from '@ant-design/icons';
import type { 
  StockLevel, 
  Warehouse, 
  StockMovement, 
  TransferOrder,
  BarcodeScanner,
  CycleCount,
  PickList,
  InventoryAdjustment 
} from '../../types';
import LotSerialTracking from '../LotSerialTracking';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

interface InventoryDashboardProps {
  warehouses: Warehouse[];
  selectedWarehouse?: string;
  companyId: string;
  onWarehouseChange: (warehouseId: string) => void;
  onStartBarcodeScan: () => void;
  onCreateTransfer: () => void;
  onCreateAdjustment: () => void;
  onCreateCycleCount: () => void;
}

export const InventoryDashboard: React.FC<InventoryDashboardProps> = ({
  warehouses,
  selectedWarehouse,
  companyId,
  onWarehouseChange,
  onStartBarcodeScan,
  onCreateTransfer,
  onCreateAdjustment,
  onCreateCycleCount,
}) => {
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState<any>(null);

  // Mock data - replace with actual API calls
  const mockDashboardData = {
    totalProducts: 1247,
    totalValue: 2456789.50,
    lowStockItems: 23,
    outOfStockItems: 5,
    pendingTransfers: 8,
    activeScanners: 12,
    todayMovements: 156,
    warehouseUtilization: 78.5,
    recentMovements: [
      {
        id: '1',
        type: 'receipt',
        productName: 'Laptop Dell XPS 13',
        quantity: 50,
        warehouse: 'Main Warehouse',
        timestamp: new Date(),
        user: 'John Doe',
      },
      {
        id: '2',
        type: 'pick',
        productName: 'iPhone 14 Pro',
        quantity: -25,
        warehouse: 'Main Warehouse',
        timestamp: new Date(),
        user: 'Jane Smith',
      },
    ],
    lowStockProducts: [
      {
        id: '1',
        name: 'Samsung Galaxy S23',
        sku: 'SAM-GS23-128',
        currentStock: 5,
        minLevel: 20,
        warehouse: 'Main Warehouse',
      },
      {
        id: '2',
        name: 'MacBook Air M2',
        sku: 'APL-MBA-M2',
        currentStock: 2,
        minLevel: 10,
        warehouse: 'Distribution Center',
      },
    ],
    activeTasks: [
      {
        id: '1',
        type: 'pick_list',
        title: 'Pick List #PL-2024-001',
        priority: 'high',
        assignedTo: 'Mike Johnson',
        dueDate: new Date(),
        progress: 75,
      },
      {
        id: '2',
        type: 'cycle_count',
        title: 'Cycle Count Zone A',
        priority: 'normal',
        assignedTo: 'Sarah Wilson',
        dueDate: new Date(),
        progress: 30,
      },
    ],
    scannerStatus: [
      {
        id: '1',
        name: 'Scanner-001',
        type: 'handheld',
        status: 'active',
        battery: 85,
        assignedTo: 'John Doe',
        lastSeen: new Date(),
      },
      {
        id: '2',
        name: 'Scanner-002',
        type: 'handheld',
        status: 'idle',
        battery: 92,
        assignedTo: 'Jane Smith',
        lastSeen: new Date(),
      },
    ],
  };

  useEffect(() => {
    setDashboardData(mockDashboardData);
  }, [selectedWarehouse]);

  const getMovementTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      receipt: 'green',
      pick: 'blue',
      transfer: 'orange',
      adjustment: 'purple',
      return: 'red',
    };
    return colors[type] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      high: 'red',
      normal: 'blue',
      low: 'green',
    };
    return colors[priority] || 'default';
  };

  const getScannerStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'green',
      idle: 'orange',
      offline: 'red',
    };
    return colors[status] || 'default';
  };

  const lowStockColumns = [
    {
      title: 'Product',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: any) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.sku}</Text>
        </div>
      ),
    },
    {
      title: 'Current Stock',
      dataIndex: 'currentStock',
      key: 'currentStock',
      render: (value: number, record: any) => (
        <div>
          <Text strong style={{ color: value <= record.minLevel ? '#ff4d4f' : '#1890ff' }}>
            {value}
          </Text>
          <Text type="secondary"> / {record.minLevel}</Text>
        </div>
      ),
    },
    {
      title: 'Warehouse',
      dataIndex: 'warehouse',
      key: 'warehouse',
    },
    {
      title: 'Action',
      key: 'action',
      render: () => (
        <Button type="link" size="small">
          Reorder
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>Inventory & Warehouse Management</Title>
          <Text type="secondary">Real-time inventory tracking and warehouse operations</Text>
        </div>
        <Space>
          <Select
            style={{ width: 200 }}
            placeholder="Select Warehouse"
            value={selectedWarehouse}
            onChange={onWarehouseChange}
          >
            <Select.Option value="">All Warehouses</Select.Option>
            {warehouses.map(warehouse => (
              <Select.Option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </Select.Option>
            ))}
          </Select>
          <Button icon={<ReloadOutlined />} onClick={() => setLoading(true)}>
            Refresh
          </Button>
          <Button type="primary" icon={<ScanOutlined />} onClick={onStartBarcodeScan}>
            Start Scanning
          </Button>
        </Space>
      </div>

      {/* Key Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Products"
              value={dashboardData?.totalProducts || 0}
              prefix={<BoxPlotOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Inventory Value"
              value={dashboardData?.totalValue || 0}
              prefix="$"
              precision={2}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Low Stock Items"
              value={dashboardData?.lowStockItems || 0}
              prefix={<AlertOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Out of Stock"
              value={dashboardData?.outOfStockItems || 0}
              prefix={<AlertOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Warehouse Operations */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Pending Transfers"
              value={dashboardData?.pendingTransfers || 0}
              prefix={<TruckOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Scanners"
              value={dashboardData?.activeScanners || 0}
              prefix={<ScanOutlined />}
              valueStyle={{ color: '#13c2c2' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Today's Movements"
              value={dashboardData?.todayMovements || 0}
              prefix={<BarChartOutlined />}
              valueStyle={{ color: '#eb2f96' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <div style={{ marginBottom: '8px' }}>
              <Text strong>Warehouse Utilization</Text>
            </div>
            <Progress
              percent={dashboardData?.warehouseUtilization || 0}
              status={dashboardData?.warehouseUtilization > 90 ? 'exception' : 'active'}
              strokeColor={dashboardData?.warehouseUtilization > 90 ? '#ff4d4f' : '#1890ff'}
            />
          </Card>
        </Col>
      </Row>

      {/* Quick Actions */}
      <Card title="Quick Actions" style={{ marginBottom: '24px' }}>
        <Space wrap>
          <Button type="primary" icon={<ScanOutlined />} onClick={onStartBarcodeScan}>
            Barcode Scanning
          </Button>
          <Button icon={<TruckOutlined />} onClick={onCreateTransfer}>
            Create Transfer
          </Button>
          <Button icon={<AlertOutlined />} onClick={onCreateAdjustment}>
            Stock Adjustment
          </Button>
          <Button icon={<BarChartOutlined />} onClick={onCreateCycleCount}>
            Cycle Count
          </Button>
          <Button icon={<PlusOutlined />}>
            Add Product
          </Button>
          <Button icon={<HomeOutlined />}>
            Manage Locations
          </Button>
          <Button icon={<ExportOutlined />}>
            Export Report
          </Button>
          <Button icon={<SettingOutlined />}>
            WMS Settings
          </Button>
        </Space>
      </Card>

      {/* Main Content Tabs */}
      <Tabs defaultActiveKey="overview">
        <TabPane tab="Overview" key="overview">
          <Row gutter={[16, 16]}>
            {/* Recent Stock Movements */}
            <Col xs={24} lg={12}>
              <Card title="Recent Stock Movements" extra={<Button type="link">View All</Button>}>
                <List
                  dataSource={dashboardData?.recentMovements || []}
                  renderItem={(item: any) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <Avatar
                            style={{
                              backgroundColor: getMovementTypeColor(item.type),
                            }}
                          >
                            {item.type === 'receipt' ? '+' : '-'}
                          </Avatar>
                        }
                        title={
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>{item.productName}</span>
                            <Tag color={getMovementTypeColor(item.type)}>
                              {item.type.toUpperCase()}
                            </Tag>
                          </div>
                        }
                        description={
                          <div>
                            <div>Qty: {Math.abs(item.quantity)} | {item.warehouse}</div>
                            <div style={{ fontSize: '12px', color: '#999' }}>
                              {item.user} • {item.timestamp.toLocaleTimeString()}
                            </div>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Col>

            {/* Low Stock Alert */}
            <Col xs={24} lg={12}>
              <Card title="Low Stock Alert" extra={<Button type="link">View All</Button>}>
                <Table
                  dataSource={dashboardData?.lowStockProducts || []}
                  columns={lowStockColumns}
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="Active Tasks" key="tasks">
          <List
            dataSource={dashboardData?.activeTasks || []}
            renderItem={(item: any) => (
              <List.Item
                actions={[
                  <Button type="link">View Details</Button>,
                  <Button type="link">Complete</Button>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      style={{
                        backgroundColor: getPriorityColor(item.priority),
                      }}
                    >
                      {item.type === 'pick_list' ? 'P' : 'C'}
                    </Avatar>
                  }
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{item.title}</span>
                      <Space>
                        <Tag color={getPriorityColor(item.priority)}>
                          {item.priority.toUpperCase()}
                        </Tag>
                        <Progress
                          type="circle"
                          size={40}
                          percent={item.progress}
                          format={() => `${item.progress}%`}
                        />
                      </Space>
                    </div>
                  }
                  description={
                    <div>
                      <div>Assigned to: {item.assignedTo}</div>
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        Due: {item.dueDate.toLocaleDateString()}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </TabPane>

        <TabPane tab="Scanner Status" key="scanners">
          <List
            dataSource={dashboardData?.scannerStatus || []}
            renderItem={(item: any) => (
              <List.Item
                actions={[
                  <Button type="link">Configure</Button>,
                  <Button type="link">Test</Button>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Badge
                      status={item.status === 'active' ? 'processing' : item.status === 'idle' ? 'warning' : 'error'}
                      dot
                    >
                      <Avatar icon={<ScanOutlined />} />
                    </Badge>
                  }
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{item.name}</span>
                      <Space>
                        <Tag color={getScannerStatusColor(item.status)}>
                          {item.status.toUpperCase()}
                        </Tag>
                        <Tooltip title="Battery Level">
                          <Progress
                            type="circle"
                            size={40}
                            percent={item.battery}
                            format={() => `${item.battery}%`}
                            strokeColor={item.battery < 20 ? '#ff4d4f' : '#52c41a'}
                          />
                        </Tooltip>
                      </Space>
                    </div>
                  }
                  description={
                    <div>
                      <div>Type: {item.type} | Assigned to: {item.assignedTo}</div>
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        Last seen: {item.lastSeen.toLocaleString()}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </TabPane>

        <TabPane tab="Lot/Serial Tracking" key="lot-serial">
          <LotSerialTracking companyId={companyId} />
        </TabPane>
      </Tabs>
    </div>
  );
}; 